import React, { useState, useEffect } from "react";

import { FileField } from "./views";
import { InputFile } from "../file-classes";
import { uploadFile } from "../file-functions";
import { accept, maximumSize } from "../operators/file";
import { resize } from "../operators/image";
import { page2Image, passwordProtected } from "../operators/pdf";
import LoadingIcon from "../../../../loading-icon/loading-icon";
import Container from "../../container";

const SingleUploadField = ({
    onChange = null,
    onDelete = null,
    identifier = null,
    operators = [passwordProtected, page2Image, resize, accept, maximumSize],
    disabled = false,
    setDisabled = null,
    ...props
}) => {
    const [selected, setSelected] = useState(props.value);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const setFileUploadingContext = () => () => {
        if (props.context) {
            if (loading)
                props.context.setValue(`${props.name}_file_uploading`, true);
            else
                if (props.context.getValue(`${props.name}_file_uploading`))
                    props.context.unsetValue(`${props.name}_file_uploading`);
        }
    };
    
    const onUploading = (uploading) => {
        setLoading(uploading);
        setDisabled && setDisabled(uploading);
    };

    const handleChange = async file => {
        onUploading(true);
        let processedFile = file;
        setError("");
        if (!file) {
            onUploading(false);
            return;
        }

        for (const operator of operators) {
            try {
                if (processedFile) {
                    processedFile = await operator(props, processedFile);
                }
            } catch (error) {
                onUploading(false);
                setError(`${error.message}`);
                processedFile = null;
                break;
            }
        }
        if (processedFile) {
            const selectedFile = new InputFile(processedFile);

            try {
                /**
                 * iOS Safari (13.1.1) appears to expire the original file data after a period of time;
                 * By storing the dataUrl for the file in state we can reconstruct it before submission.
                 */
                await selectedFile.dataUrl();

                if (props.multipleFiles) {
                    const result = await uploadFile(processedFile, props.id);
                    selectedFile.uploadedFileData = result;
                    selectedFile.multipleFiles = true;

                    selectedFile.setUniqueId();

                    onUploading(false);
                    setSelected(selectedFile);
                    onChange &&
                        onChange(
                            selectedFile,
                            selected ? selected.identifier : undefined
                        );
                } else {
                    onUploading(false);
                    setSelected(selectedFile);
                    onChange && onChange(selectedFile);
                }
            } catch (error) {
                onUploading(false);
                setError(error.message);
                processedFile = null;
            }
        }
    };

    const handleDelete = () => {
        setSelected("");
        setError("");
        if (props.multipleFiles) {
            onDelete && onDelete(identifier);
        } else {
            onChange && onChange(undefined);
            onDelete && onDelete();
        }
    };

    useEffect(setFileUploadingContext(), [loading]);

    return !loading ? (
        <FileField
            {...props}
            identifier={identifier}
            value={selected}
            error={error || props.error}
            disabled={disabled}
            onChange={files => {
                handleChange(files[0]);
            }}
            onDelete={handleDelete}
        />
    ) : (
        <Container cssClass={"u-p-0" +
            (props.multipleFiles && identifier !== undefined
                ? " u-mb-2"
                : ""
            )}
        >
            <LoadingIcon my={5} />
        </Container>
    );
};

export default SingleUploadField;